<template>
  <div class="metadata-modal">
    <div class="body" ref="body">
      <type-field 
        :root="rootNode" 
        :type="formData.type" 
        :limitCompType="limitCompType"
      ></type-field>
      <div class="input-block grid-col-1-3 grid-row-2-3">
        <label>
          Label
          <span class="required">*</span>
          <tooltip :text="TOOLTIP_TEXT.INPUT_LABEL"></tooltip>
        </label>
        <input
          type="text"
          v-model="formData.label"
          name="label"
          required
          @blur="generateFieldName()"
        />
      </div>
      <div class="input-block grid-row-2-3">
        <label>
          Hint
          <tooltip :text="TOOLTIP_TEXT.HINT"></tooltip>
        </label>
        <input type="text" v-model="formData.hint" name="hint" />
      </div>
      <div class="checkbox-block-group grid-col-1-3 grid-row-3-4">
        <div class="checkbox-block">
          <input
            type="checkbox"
            name="required"
            id="metadata-checkbox-1"
            v-model="formData.required"
            @change="checkboxOnChange($event)"
            class="checkbox-input"
          />
          <label for="metadata-checkbox-1">
            Required
            <tooltip :text="TOOLTIP_TEXT.REQUIRED"></tooltip>
          </label>
        </div>
        <div class="checkbox-block">
          <input
            type="checkbox"
            name="showReqHint"
            id="metadata-checkbox-2"
            v-model="formData.showReqHint"
            @change="checkboxOnChange($event)"
            class="checkbox-input"
          />
          <label for="metadata-checkbox-2">
            Show Req Hint
            <tooltip :text="TOOLTIP_TEXT.SHOW_REQ_HINT"></tooltip>
          </label>
        </div>
        <div class="checkbox-block">
          <input
            type="checkbox"
            name="showFieldLabel"
            id="metadata-checkbox-3"
            v-model="formData.showFieldLabel"
            @change="checkboxOnChange($event)"
            class="checkbox-input"
          />
          <label for="metadata-checkbox-3">
            Show Field Label
            <tooltip :text="TOOLTIP_TEXT.SHOW_FIELD_LABEL"></tooltip>
          </label>
        </div>
      </div>
      <div class="input-block grid-col-1-2 grid-row-4-5">
        <label>
          Field Name (auto-populated)
          <span class="required">*</span>
          <tooltip :text="TOOLTIP_TEXT.FIELD_NAME"></tooltip>
        </label>
        <input
          type="text"
          v-model="formData.fieldName"
          name="fieldName"
          required
          disabled
        />
      </div>
      <form class="input-block extensions-block grid-col-2-3 grid-row-4-5" @submit="addExtensions($event)">
        <label class="grid-row-1-2">
          File Type
          <tooltip :text="TOOLTIP_TEXT.FILE_TYPE"></tooltip>
        </label>
        <select
          type="text"
          name="extensions"
          class="grid-row-2-3"
        >
          <option value="" selected disabled hidden></option>
          <option
            v-for="(value, extension) in EXTENSIONS"
            :key="extension"
            :value="value"
          >
            {{ extension }}
          </option>
        </select>
        <button type="submit" class="extensions-button grid-row-2-3">Add</button>
        <div class="extensions" v-if="formData.extensions">
          <p class="extensions-tag" v-for="extension in extensionList"
          :key="extension">
          {{ extension }} <span class="remove" @click="removeExtensions(extension)">x</span>
          </p>
        </div>
      </form>
      <div class="grid-col-1-4 grid-row-5-6">
        <slot></slot>
      </div>
      <fieldset v-if="!simpleView" class="more-option grid-col-1-4 grid-row-6-7">
        <legend>More Options</legend>
      </fieldset>
      <dependency-editor
        v-if="!simpleView"
        class="grid-col-1-3 grid-row-7-8"
        :dependency="getDependency()"
        :fieldName="this.formData.fieldName"
        @addDependency="addDependency($event)"
        @removeDependency="removeDependency($event)"
      ></dependency-editor>
      <div class="input-block grid-row-7-8" v-if="!simpleView">
        <label>
          Label Width
          <tooltip :text="TOOLTIP_TEXT.LABEL_WIDTH"></tooltip>
        </label>
        <input
          type="number"
          v-model="formData.labelWidth"
          name="labelWidth"
          class="input-short"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { FILE_EXTENSIONS, ROOT_NODE, TOOLTIP_TEXT } from "../mixins/constant-mixin";
import { populateFieldName } from "../mixins/component-utils-mixin";
import TypeField from "./TypeField.vue";
import Tooltip from "./Tooltip.vue";
import DependencyEditor from "./DependencyEditor.vue";
import { Bus } from "../main";

export default {
  name: "FileModal",
  props: {
    metadata: {
      type: Object,
      required: true,
    },
    state: {
      type: Object,
    },
    simpleView: {
      type: Boolean,
    },
    limitCompType: {
      type: String,
    },
  },
  data() {
    return {
      formData: structuredClone(this.metadata),
      rootNode: ROOT_NODE.FIELDS,
      TOOLTIP_TEXT: TOOLTIP_TEXT,
      EXTENSIONS: FILE_EXTENSIONS,
      populateFieldName: populateFieldName,
    };
  },
  computed: {
    extensionList() {
      const extensionList = this.formData.extensions.split(",");
      extensionList.forEach(extension => extension = extension.trim());
      return extensionList;
    }
  },
  watch: {
    metadata: function () {
      this.formData = structuredClone(this.metadata);
    },
    state: function () {
      if (this.state.closing) {
        if (this.state.saving) {
          this.$emit("metadataSaved", this.formData);
        } else {
          this.formData = structuredClone(this.metadata);
        }
      }
    },
    simpleView: function () {
      this.insertIntoComponentModal();
    },
  },
  components: {
    "type-field": TypeField,
    "tooltip": Tooltip,
    "dependency-editor": DependencyEditor,
  },
  mounted() {
    this.insertIntoComponentModal();

    // default value
    if (!this.formData.hasOwnProperty("labelWidth")) {
      this.$set(this.formData, "labelWidth", 12);
    }
    if (!this.formData.hasOwnProperty("showFieldLabel")) {
      this.$set(this.formData, "showFieldLabel", true);
    }

    Bus.$on("sendDupFieldName", (list) => this.checkDupFieldName(list));
  },
  beforeDestroy() {
    Bus.$off("sendDupFieldName");
  },
  methods: {
    getDependency: function () {
      if (this.formData.dependency) {
        return this.formData.dependency;
      }
      return { parent: "", displayIfValues: [] };
    },
    addDependency: function (obj) {
      if (!this.formData.dependency) {
        this.formData.dependency = { parent: "", displayIfValues: [] };
      }
      this.formData.dependency.parent = obj.parent;
      this.formData.dependency.displayIfValues = obj.displayIfValues;
    },
    checkboxOnChange: function (event) {
      this.formData[event.target.name] = event.target.checked;
    },
    removeDependency: function () {
      this.$delete(this.formData, "dependency");
    },
    getCompType: function (type) {
      return type.toLowerCase() === this.formData.type.toLowerCase();
    },
    insertIntoComponentModal: function () {
      const gridRow = "3 / 4";
      Bus.$emit("insertIntoComponentModal", this.$refs.body, gridRow);
    },
    // 1) Populate field name
    // 2) Emit event to FormEditor to check duplicate
    // 3) FormEditor send the list of duplicate field name using Bus
    // 4) If the list is not empty, adjust the field name
    generateFieldName: function () {
      const fieldName = populateFieldName(this.formData, 'label');
      if (fieldName) {
        Bus.$emit("getDupFieldName", fieldName);
      }
    },
    // If there is duplicate field name, append field name with counter
    checkDupFieldName: function (list) {
      const fieldName = this.formData.fieldName;
      if (list.length > 0) {
        let count = 1;
        let temp = fieldName ;
        while (list.find(name => name == temp)) {
          temp = fieldName + (count++);
        }
        count--;
        this.formData.fieldName = temp;
      }
    },
    addExtensions: function (event) {
      event.preventDefault();
      const value = event.target.querySelector("select").value;
      if (!value || value.trim() === "") {
        return;
      }
      if (!this.formData.extensions) {
        this.$set(this.formData, "extensions", "");
      } else if (this.formData.extensions.match(value)) {
        event.target.querySelector("select").value = "";
        return;
      } else if (this.formData.extensions.length > 0) {
        this.formData.extensions += ", ";
      }
      this.formData.extensions += value;
      event.target.querySelector("select").value = "";
      // to standardize the separator so it is unlikely to cause error and looks more neat
      this.formData.extensions = this.formData.extensions.replace(/\s*\,\s*/g, ", ");
    },
    removeExtensions: function (extension) {
      const target = extension.trim();
      let position = -1;
      this.formData.extensions.split(", ").some((ext, index) => {
        if (ext.trim() === target) {
          position = index;
          return true; // break the some loop
        }
      })
      const regex = new RegExp();
      if (position === 0) {
        regex.compile(`${target}\\,*\\s*`);
      } else {
        regex.compile(`\\,\\s*${target}`);
      }
      this.formData.extensions = this.formData.extensions.replace(regex, "");
    },
  },
};
</script>

<style scoped>
.metadata-modal {
  width: 100%;
}

.body {
  padding: 1rem 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-row-gap: 1rem;
}

.body > div,
.body > form {
  padding: 0 2rem;
}

.input-block {
  display: flex;
  flex-direction: column;
}

.input-block input,
.input-block select {
  padding: 8px;
  margin-top: 4px;
}

.input-short {
  text-align: center;
  width: 6rem;
}

/* Hide arrow in number input */
/* Chrome and safari */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.extensions-block {
  display: grid;
  column-gap: 1rem;
}

.extensions-button {
  margin: 0;
  margin-top: 4px;
  padding: 8px 16px;
  align-self: center;
  place-self: center;
  border: 1px solid #000;
  border-radius: 5px;
  font-size: 12px;
  background: #f0f0f0;
  cursor: pointer;
}

.extensions-button:active {
  background: #e0e0e0;
}

.extensions {
  grid-column: 1 / 3;
  grid-row: 3 / 4;
  display: flex;
  flex-wrap: wrap;
}

.extensions-tag {
  border: 1px solid #c0c0c0;
  border-radius: 10px;
  width: fit-content;
  font-size: 12px;
  padding: 2px 8px;
  margin-right: 1rem;
  margin-bottom: 0;
}

.remove {
  text-align: center;
  background: #fff;
  padding: 0 2px 2px 2px;
  margin-top: 2px;
  margin-left: 5px;
  font-weight: bolder;
  cursor: pointer;
}

.checkbox-block-group {
  display: flex;
}

.checkbox-block {
  align-self: center;
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
  margin-right: 30px;
}

.checkbox-input {
  margin-right: 8px;
  width: 16px;
  height: 16px;
}

.grid-col-1-2 {
  grid-column: 1 / 2;
}

.grid-col-1-3 {
  grid-column: 1 / 3;
}

.grid-col-1-4 {
  grid-column: 1 / 4;
}

.grid-col-2-3 {
  grid-column: 2 / 3;
}

.grid-col-3-4 {
  grid-column: 3 / 4;
}

.grid-row-1-2 {
  grid-row: 1 / 2;
}

.grid-row-2-3 {
  grid-row: 2 / 3;
}

.grid-row-3-4 {
  grid-row: 3 / 4;
}

.grid-row-4-5 {
  grid-row: 4 / 5;
}

.grid-row-4-6 {
  grid-row: 4 / 6;
}

.grid-row-5-6 {
  grid-row: 5 / 6;
}

.grid-row-6-7 {
  grid-row: 6 / 7;
}

.grid-row-7-8 {
  grid-row: 7 / 8;
}

.more-option {
  margin: 0;
  padding: 0;
  border: none;
  border-top: 1px solid #808080;
  text-align: center;
}

.more-option > legend {
  padding: 0 0.5rem;
}

.required {
  color: #f00;
}
</style>